import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PlusCircle, ListChecks, PackageSearch, AlertTriangle, Hourglass, FileText, Filter, History, Search, Zap } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { URGENCY } from "@/lib/constants";
import Navbar from "@/components/Navbar";
import NewRequestForm from "@/components/NewRequestForm";
import RequestCard from "@/components/RequestCard";
import RequestDetailDialog from "@/components/RequestDetailDialog";
import FlashOffers from "@/components/FlashOffer";
import OfferAdmin from "@/components/OfferAdmin";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3" data-testid={`stat-${label}`}>
    <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${color}`}><Icon className="h-5 w-5" /></div>
    <div>
      <div className="text-2xl font-heading font-extrabold leading-none">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  </div>
);

export default function DashboardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ total: 0, urgent: 0, pending: 0, quoted: 0 });
  const [tab, setTab] = useState(isAdmin ? "list" : "new");
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [histValue, setHistValue] = useState("");
  const [histResults, setHistResults] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [offerCount, setOfferCount] = useState(0);

  useEffect(() => {
    if (isAdmin) return;
    api.get("/offers/unseen").then(({ data }) => setOfferCount(data.count)).catch(() => {});
  }, [isAdmin]);

  const onBellClick = () => {
    if (isAdmin || offerCount === 0) return;
    setOfferCount(0);
    api.post("/offers/seen").catch(() => {});
    document.getElementById("flash-offers")?.scrollIntoView({ behavior: "smooth" });
  };

  const load = useCallback(async () => {
    const [r, s] = await Promise.all([api.get("/requests"), api.get("/requests/stats")]);
    setRequests(r.data);
    setStats(s.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return requests.filter((r) => {
      if (filter !== "all" && r.urgency !== filter) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${r.part_name} ${r.part_reference} ${r.vehicle?.marque} ${r.vehicle?.modele} ${r.vehicle?.input_value}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [requests, filter, search]);

  const notifCount = useMemo(
    () => requests.filter((r) => (isAdmin ? r.notify_admin : r.notify_garage)).length,
    [requests, isAdmin]
  );

  const runHistory = async () => {
    if (!histValue.trim()) return;
    setHistLoading(true);
    try {
      const { data } = await api.get("/vehicle/history", { params: { value: histValue } });
      setHistResults(data.requests || []);
    } catch {
      setHistResults([]);
    } finally {
      setHistLoading(false);
    }
  };

  const onUpdated = (updated) => {
    setRequests((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setSelected(updated);
    load();
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar notifCount={notifCount} offerCount={offerCount} onBellClick={onBellClick} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
              {isAdmin ? "Console fournisseur" : "Tableau de bord"}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isAdmin ? "Traitez les demandes entrantes et envoyez vos devis." : "Recherchez une pièce par plaque ou châssis et suivez vos demandes."}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={PackageSearch} label="Demandes" value={stats.total} color="bg-blue-500/10 text-blue-600" />
          <StatCard icon={AlertTriangle} label="Urgentes" value={stats.urgent} color="bg-red-500/10 text-red-600" />
          <StatCard icon={Hourglass} label="En attente" value={stats.pending} color="bg-amber-500/10 text-amber-600" />
          <StatCard icon={FileText} label="Devis reçus" value={stats.quoted} color="bg-emerald-500/10 text-emerald-600" />
        </div>

        {!isAdmin && <FlashOffers />}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto">
            {!isAdmin && <TabsTrigger value="new" data-testid="tab-new"><PlusCircle className="h-4 w-4 mr-2" />Nouvelle demande</TabsTrigger>}
            <TabsTrigger value="list" data-testid="tab-list"><ListChecks className="h-4 w-4 mr-2" />{isAdmin ? "Demandes entrantes" : "Mes demandes"}</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history"><History className="h-4 w-4 mr-2" />Historique véhicule</TabsTrigger>
            {isAdmin && <TabsTrigger value="offer" data-testid="tab-offer"><Zap className="h-4 w-4 mr-2" />Offre du mois</TabsTrigger>}
          </TabsList>

          {!isAdmin && (
            <TabsContent value="new" className="mt-6">
              <div className="max-w-3xl">
                <NewRequestForm onCreated={() => { load(); setTab("list"); }} />
              </div>
            </TabsContent>
          )}

          <TabsContent value="list" className="mt-6">
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <div className="relative flex-1 max-w-sm">
                <Input data-testid="search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher pièce, plaque, modèle..." className="pl-3" />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <button data-testid="filter-all" onClick={() => setFilter("all")} className={`px-3 py-1.5 rounded-full text-sm font-medium border ${filter === "all" ? "bg-blue-700 text-white border-blue-700" : "border-border"}`}>Toutes</button>
                {Object.values(URGENCY).map((u) => (
                  <button key={u.key} data-testid={`filter-${u.key}`} onClick={() => setFilter(u.key)} className={`px-3 py-1.5 rounded-full text-sm font-medium border ${filter === u.key ? "bg-blue-700 text-white border-blue-700" : "border-border"}`}>{u.short}</button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20 bg-card border border-dashed border-border rounded-2xl">
                <PackageSearch className="h-12 w-12 mx-auto text-muted-foreground/40" />
                <p className="mt-4 font-medium">Aucune demande</p>
                <p className="text-sm text-muted-foreground">{isAdmin ? "Les demandes des garages apparaîtront ici." : "Créez votre première demande de pièce."}</p>
              </motion.div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((r) => (
                  <RequestCard key={r.id} req={r} isAdmin={isAdmin} onClick={() => setSelected(r)} />
                ))}
              </div>
            )}
          </TabsContent>

          {isAdmin && (
            <TabsContent value="offer" className="mt-6">
              <OfferAdmin />
            </TabsContent>
          )}

          <TabsContent value="history" className="mt-6">
            <div className="max-w-3xl">
              <div className="bg-card border border-border rounded-2xl p-5 sm:p-7 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <History className="h-5 w-5 text-blue-600" />
                  <h3 className="font-heading font-bold text-lg">Historique d'un véhicule</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">Recherchez par plaque d'immatriculation ou numéro de châssis (VIN) pour retrouver toutes les demandes passées.</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input data-testid="history-search-input" value={histValue} onChange={(e) => setHistValue(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && runHistory()} placeholder="Ex : AA-123-BB ou VF7..." className="h-11 font-mono tracking-widest uppercase" />
                  <Button data-testid="history-search-btn" onClick={runHistory} disabled={histLoading} className="h-11 bg-blue-700 hover:bg-blue-800 px-6">
                    <Search className="h-4 w-4 mr-2" />{histLoading ? "Recherche..." : "Rechercher"}
                  </Button>
                </div>
              </div>

              {histResults !== null && (
                histResults.length === 0 ? (
                  <div className="text-center py-16 bg-card border border-dashed border-border rounded-2xl">
                    <PackageSearch className="h-12 w-12 mx-auto text-muted-foreground/40" />
                    <p className="mt-4 font-medium">Aucune demande pour ce véhicule</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm font-medium text-muted-foreground">{histResults.length} demande(s) trouvée(s)</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {histResults.map((r) => (
                        <RequestCard key={r.id} req={r} isAdmin={isAdmin} onClick={() => setSelected(r)} />
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <RequestDetailDialog
        req={selected}
        isAdmin={isAdmin}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
        onUpdated={onUpdated}
      />
    </div>
  );
}
